/**
 * Production-Safe Modal Cleanup Utilities
 *
 * Handles pointer-events cleanup for modals/sheets in production builds.
 * Production builds don't have React Strict Mode safety net, so react-remove-scroll
 * can fail to clean up properly, leaving pointer-events: none stuck.
 *
 * Based on audiences best practices.
 */

/**
 * Remove stuck pointer-events from document.body
 * Should be called when modals/sheets close to ensure cleanup
 *
 * @param context - Context string for logging (e.g., component name)
 * @param logInProduction - Whether to log in production (default: false)
 */
export function cleanupPointerEvents(
  context: string = 'Modal',
  logInProduction: boolean = false
): void {
  const bodyStyle = document.body.style;
  if (bodyStyle.pointerEvents === 'none') {
    bodyStyle.pointerEvents = '';
    bodyStyle.removeProperty('pointer-events');

    if (process.env.NODE_ENV === 'production' && logInProduction) {
      console.warn(`[${context}] Removed stuck pointer-events in production`);
    }
  }
}

/**
 * Create a cleanup function that runs after animation delay
 * Useful for ensuring cleanup happens after modal/sheet close animation completes
 *
 * @param delay - Delay in milliseconds (default: 350ms for sheets, 200ms for modals)
 * @param context - Context string for logging
 * @returns Cleanup function
 */
export function createDelayedCleanup(
  delay: number = 350,
  context: string = 'Modal'
): () => void {
  return () => {
    setTimeout(() => {
      cleanupPointerEvents(context, true);
    }, delay);
  };
}

/**
 * Hook for page-level modal cleanup
 * Watches multiple modal states and cleans up if any are stuck
 *
 * NOTE: This is a hook, so it must be used in a React component.
 * Import React in your component file when using this hook.
 *
 * @example
 * ```tsx
 * import { useModalCleanup } from '@/components/shared/utils/modalCleanup';
 * import { useEffect } from 'react';
 *
 * function MyPage() {
 *   const [modal1Open, setModal1Open] = useState(false);
 *   const [modal2Open, setModal2Open] = useState(false);
 *
 *   // Production-safe cleanup
 *   useEffect(() => {
 *     const cleanup = () => {
 *       cleanupPointerEvents('MyPage', true);
 *     };
 *     return cleanup;
 *   }, [modal1Open, modal2Open]);
 *
 *   return (/* ... *\/);
 * }
 * ```
 */
